import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { Atom } from 'react-loading-indicators';

const CRM_PER_PAGE = 100;

const TYPE_OPTIONS = ['Buyer', 'Seller'];

const BUDGET_RANGE_OPTIONS = [
  '$0 - $25k',
  '$25k - $50k',
  '$50k - $100k',
  '$100k - $250k',
  '$250k - $500k',
  '$500k - $1M',
  '$1M - $2.5M',
  '$2.5M - $10M',
  '$10M - $20M',
  '$20M+',
];

/** Fixed column order and headers for CRM results table. */
const CRM_TABLE_COLUMNS = [
  { key: 'creation_date', label: 'Created' },
  { key: 'first_name', label: 'First name' },
  { key: 'last_name', label: 'Last name' },
  { key: 'email', label: 'Email' },
  { key: 'is_buyer', label: 'Is buyer' },
  { key: 'is_seller', label: 'Is seller' },
  { key: 'is_sba_only', label: 'SBA only' },
  { key: 'nda_status', label: 'NDA status' },
  { key: 'qualification', label: 'Qualification' },
  { key: 'niche', label: 'Niches' },
  { key: 'budget_range', label: 'Budget range' },
  { key: 'source', label: 'Source' },
  { key: 'last_active', label: 'Last active' },
];

/** Extra cell styles keyed by `CRM_TABLE_COLUMNS[].key` (e.g. cap wide text columns). */
const CRM_TABLE_COLUMN_CELL_STYLE = {
  niche: { width: '200px', maxWidth: '200px' },
};

function formatCellValue(value) {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** Show YYYY-MM-DD only (API may send ISO datetime strings). */
function formatCreationDateOnly(value) {
  if (value === null || value === undefined || value === '') return '';
  const s = String(value).trim();
  if (!s) return '';
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const t = Date.parse(s);
  if (!Number.isNaN(t)) {
    return new Date(t).toISOString().slice(0, 10);
  }
  return s;
}

function parseContentDispositionFilename(contentDisposition) {
  if (!contentDisposition || typeof contentDisposition !== 'string') {
    return 'crm_contacts.csv';
  }
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1].replace(/['"]/g, '').trim());
    } catch {
      return 'crm_contacts.csv';
    }
  }
  const quoted = contentDisposition.match(/filename="([^"]+)"/i);
  if (quoted) return quoted[1].trim();
  const unquoted = contentDisposition.match(/filename=([^;]+)/i);
  if (unquoted) return unquoted[1].replace(/['"]/g, '').trim();
  return 'crm_contacts.csv';
}

function formatCrmTableCell(row, columnKey) {
  if (columnKey === 'creation_date') {
    return formatCreationDateOnly(row.creation_date);
  }
  if (columnKey === 'last_active') {
    return formatCreationDateOnly(row.last_active);
  }
  if (columnKey === 'niche') {
    const nicheStr = row.niche != null ? String(row.niche).trim() : '';
    const raw = nicheStr !== '' ? row.niche : row.niche_list;
    return formatCellValue(raw);
  }
  return formatCellValue(row[columnKey]);
}

const NICHE_OPTIONS = [
  'Affiliate',
  'App',
  'Apps (Marketplace)',
  'Digital Products',
  'Digital Services',
  'Direct Advertising',
  'Display Advertising',
  'Domain',
  'e-Commerce',
  'e-Commerce (Amazon FBA)',
  'e-Commerce (Shopify)',
  'Lead Generation',
  'Membership/Platform',
  'Not Monetized',
  'Other',
  'SaaS',
  'Software',
];

function CrmSearch() {
  const [types, setTypes] = useState([]);
  const [budgetRanges, setBudgetRanges] = useState([]);
  const [niches, setNiches] = useState([]);
  const [nda, setNda] = useState('');
  const [qualification, setQualification] = useState('');
  const [isSbaOnly, setIsSbaOnly] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [crmPage, setCrmPage] = useState(1);
  const [exportLoading, setExportLoading] = useState(false);

  const styles = {
    pageRoot: {
      width: '100%',
      maxWidth: '100vw',
      boxSizing: 'border-box',
      padding: '10px clamp(16px, 4vw, 40px) 32px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
    },
    formContainer: () => ({
      width: '100%',
      maxWidth: '560px',
      margin: '0 auto',
      transition: 'all 0.5s ease',
      padding: '20px',
      boxSizing: 'border-box',
    }),
    form: {
      display: 'flex',
      flexDirection: 'column',
    },
    headerText: {
      fontFamily: 'Arial, sans-serif',
      fontSize: '14px',
      marginTop: '10px',
      marginBottom: '20px',
      borderBottom: '1px solid black',
    },
    select: {
      margin: '5px 0',
      padding: '7px',
      borderRadius: '4px',
      border: '1px solid #cccccc',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      backgroundColor: 'white',
    },
    label: {
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      marginTop: '10px',
      marginBottom: '2px',
      fontWeight: 'bold',
    },
    button: {
      margin: '10px 0',
      padding: '10px',
      backgroundColor: '#6487dc',
      color: 'white',
      border: 'none',
      cursor: 'pointer',
      borderRadius: '4px',
    },
    resultsBar: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      marginBottom: '10px',
    },
    exportButton: {
      padding: '8px 14px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      borderRadius: '4px',
      border: '1.5px solid #6487dc',
      backgroundColor: '#fff',
      color: '#6487dc',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    exportButtonDisabled: {
      opacity: 0.45,
      cursor: 'not-allowed',
    },
    checkboxGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      margin: '5px 0',
      padding: '8px',
      border: '1px solid #cccccc',
      borderRadius: '4px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
    },
    checkboxGroupTwoCol: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '6px',
      margin: '5px 0',
      padding: '8px',
      border: '1px solid #cccccc',
      borderRadius: '4px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
    },
    checkboxRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    checkboxInput: {
      margin: 0,
      cursor: 'pointer',
    },
    resultsSection: {
      width: '100%',
      marginTop: '24px',
      boxSizing: 'border-box',
      flex: '1 1 auto',
    },
    resultsMeta: {
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      marginBottom: '8px',
      color: '#333',
    },
    tableWrap: {
      width: '100%',
      overflowX: 'auto',
      border: '1px solid #cccccc',
      borderRadius: '4px',
      backgroundColor: '#fafafa',
      WebkitOverflowScrolling: 'touch',
    },
    table: {
      width: '100%',
      tableLayout: 'auto',
      borderCollapse: 'collapse',
      fontFamily: 'Arial, sans-serif',
      fontSize: '11px',
    },
    th: {
      textAlign: 'left',
      padding: '8px 10px',
      borderBottom: '2px solid #6487dc',
      backgroundColor: '#eef2fb',
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
      position: 'sticky',
      top: 0,
    },
    td: {
      padding: '8px 10px',
      borderBottom: '1px solid #e0e0e0',
      verticalAlign: 'top',
      wordBreak: 'break-word',
    },
    pagi: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      marginTop: '20px',
      flexWrap: 'wrap',
    },
    pagiBtn: {
      padding: '8px 14px',
      border: '1.5px solid #ccc',
      borderRadius: '4px',
      background: '#fff',
      fontFamily: 'Arial, sans-serif',
      fontSize: '11px',
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: '#555',
      cursor: 'pointer',
    },
    pagiBtnDisabled: {
      opacity: 0.4,
      cursor: 'not-allowed',
    },
    pagiInfo: {
      fontFamily: 'Arial, sans-serif',
      fontSize: '11px',
      color: '#555',
      padding: '0 8px',
    },
    trEven: {
      backgroundColor: '#fff',
    },
    trOdd: {
      backgroundColor: '#f7f9fc',
    },
  };

  const toggleMulti = (value, setList) => {
    setList((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const buildCrmSearchPayload = () => ({
    types,
    budgetRanges,
    niches,
    nda,
    qualification,
    isSbaOnly,
  });

  const handleExportCsv = () => {
    setExportLoading(true);
    axios
      .post('/api/export_crm_data', buildCrmSearchPayload(), {
        responseType: 'blob',
        headers: { 'Content-Type': 'application/json' },
      })
      .then((response) => {
        const cd = response.headers['content-disposition'];
        const filename = parseContentDispositionFilename(cd);
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error('Error exporting CRM CSV:', error);
        alert('Could not export CSV. Please try again.');
      })
      .finally(() => {
        setExportLoading(false);
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setResults([]);
    setCrmPage(1);

    axios
      .post('/api/crm_search', buildCrmSearchPayload(), {
        headers: { 'Content-Type': 'application/json' },
      })
      .then((response) => {
        setIsLoading(false);
        setHasSearched(true);
        if (response.status === 200 && Array.isArray(response.data)) {
          setResults(response.data);
          setCrmPage(1);
        } else {
          setResults([]);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setResults([]);
        setHasSearched(false);
        console.error('Error submitting CRM search:', error);
        alert('Error submitting CRM search. Please try again.');
      });
  };

  const crmTotalPages = Math.max(1, Math.ceil(results.length / CRM_PER_PAGE));
  const crmPageSafe = Math.min(Math.max(1, crmPage), crmTotalPages);
  const crmSlice = useMemo(() => {
    const start = (crmPageSafe - 1) * CRM_PER_PAGE;
    return results.slice(start, start + CRM_PER_PAGE);
  }, [results, crmPageSafe]);

  const rangeStart = results.length === 0 ? 0 : (crmPageSafe - 1) * CRM_PER_PAGE + 1;
  const rangeEnd = Math.min(results.length, crmPageSafe * CRM_PER_PAGE);

  return (
    <div style={styles.pageRoot}>
      <div style={styles.formContainer()}>
        <form onSubmit={handleSubmit} style={styles.form}>
          <h3 style={styles.headerText}>CRM Search</h3>

          <label style={styles.label}>Types</label>
          <div style={styles.checkboxGroup}>
            {TYPE_OPTIONS.map((opt) => (
              <label key={opt} style={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={types.includes(opt)}
                  onChange={() => toggleMulti(opt, setTypes)}
                  style={styles.checkboxInput}
                />
                <span>{opt}</span>
              </label>
            ))}
          </div>

          <label style={styles.label}>Budget Range</label>
          <div style={styles.checkboxGroupTwoCol}>
            {BUDGET_RANGE_OPTIONS.map((opt) => (
              <label key={opt} style={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={budgetRanges.includes(opt)}
                  onChange={() => toggleMulti(opt, setBudgetRanges)}
                  style={styles.checkboxInput}
                />
                <span>{opt}</span>
              </label>
            ))}
          </div>

          <label style={styles.label}>Niches</label>
          <div style={styles.checkboxGroupTwoCol}>
            {NICHE_OPTIONS.map((opt) => (
              <label key={opt} style={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={niches.includes(opt)}
                  onChange={() => toggleMulti(opt, setNiches)}
                  style={styles.checkboxInput}
                />
                <span>{opt}</span>
              </label>
            ))}
          </div>

          <label style={styles.label}>NDA</label>
          <select
            value={nda}
            onChange={(e) => setNda(e.target.value)}
            style={styles.select}
          >
            <option value="">Select NDA status</option>
            <option value="valid">valid</option>
            <option value="pending">pending</option>
            <option value="invalid">invalid</option>
          </select>

          <label style={styles.label}>Qualification</label>
          <select
            value={qualification}
            onChange={(e) => setQualification(e.target.value)}
            style={styles.select}
          >
            <option value="">Select qualification</option>
            <option value="Qualified">Qualified</option>
            <option value="Pending Qualification">Pending Qualification</option>
            <option value="Unqualifed">Unqualifed</option>
          </select>

          <label style={styles.label}>Is Sba Only</label>
          <select
            value={isSbaOnly}
            onChange={(e) => setIsSbaOnly(e.target.value)}
            style={styles.select}
          >
            <option value="">Select</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>

          {isLoading && (
            <div align="center" style={{ marginTop: '20px' }}>
              <Atom color="#32cd32" size="medium" text="Searching..." />
            </div>
          )}

          <button type="submit" disabled={isLoading} style={styles.button}>
            Search CRM
          </button>
        </form>
      </div>

      <div style={styles.resultsSection}>
          {!isLoading && hasSearched && results.length === 0 && (
            <div style={styles.resultsBar}>
              <p style={{ ...styles.resultsMeta, margin: 0 }}>
                No contacts match your filters.
              </p>
              <button
                type="button"
                disabled={exportLoading}
                onClick={handleExportCsv}
                style={{
                  ...styles.exportButton,
                  ...(exportLoading ? styles.exportButtonDisabled : {}),
                }}
              >
                {exportLoading ? 'Exporting…' : 'Export CSV'}
              </button>
            </div>
          )}
          {!isLoading && results.length > 0 && (
            <>
              <div style={styles.resultsBar}>
                <p style={{ ...styles.resultsMeta, margin: 0 }}>
                  {results.length} contact{results.length === 1 ? '' : 's'} found · Showing{' '}
                  {rangeStart}–{rangeEnd}
                </p>
                <button
                  type="button"
                  disabled={exportLoading}
                  onClick={handleExportCsv}
                  style={{
                    ...styles.exportButton,
                    ...(exportLoading ? styles.exportButtonDisabled : {}),
                  }}
                >
                  {exportLoading ? 'Exporting…' : 'Export CSV'}
                </button>
              </div>
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      {CRM_TABLE_COLUMNS.map((col) => (
                        <th
                          key={col.key}
                          style={{ ...styles.th, ...CRM_TABLE_COLUMN_CELL_STYLE[col.key] }}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {crmSlice.map((row, rowIndex) => (
                      <tr
                        key={(crmPageSafe - 1) * CRM_PER_PAGE + rowIndex}
                        style={rowIndex % 2 === 0 ? styles.trEven : styles.trOdd}
                      >
                        {CRM_TABLE_COLUMNS.map((col) => (
                          <td
                            key={col.key}
                            style={{ ...styles.td, ...CRM_TABLE_COLUMN_CELL_STYLE[col.key] }}
                          >
                            {formatCrmTableCell(row, col.key)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {results.length > CRM_PER_PAGE && (
                <div style={styles.pagi}>
                  <button
                    type="button"
                    style={{
                      ...styles.pagiBtn,
                      ...(crmPageSafe <= 1 || isLoading ? styles.pagiBtnDisabled : {}),
                    }}
                    disabled={crmPageSafe <= 1 || isLoading}
                    onClick={() => setCrmPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <span style={styles.pagiInfo}>
                    Page {crmPageSafe} of {crmTotalPages}
                  </span>
                  <button
                    type="button"
                    style={{
                      ...styles.pagiBtn,
                      ...(crmPageSafe >= crmTotalPages || isLoading
                        ? styles.pagiBtnDisabled
                        : {}),
                    }}
                    disabled={crmPageSafe >= crmTotalPages || isLoading}
                    onClick={() => setCrmPage((p) => Math.min(crmTotalPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
      </div>
    </div>
  );
}

export default CrmSearch;
